import { useState, type FormEvent } from 'react';
import { DefaultAssigneesCard } from './DefaultAssigneesCard';
import { Link } from 'react-router-dom';
import { SYSTEM_ROLES, type SystemRoleName, type TeamMember } from '@neuroedge/shared';
import { Button, Card, PageState, Select, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAuth } from '../../auth/AuthContext';
import {
  useInviteMember,
  useMembers,
  useRemoveMember,
  useResendInvite,
  useUpdateMember,
} from '../../api/users';

const ROLE_OPTIONS = SYSTEM_ROLES.map((r) => ({ value: r.name, label: r.name }));

const STATUS_BADGE_STYLE: Partial<
  Record<TeamMember['status'], { borderColor: string; color: string }>
> = {
  ACTIVE: { borderColor: 'var(--color-success)', color: 'var(--color-success)' },
  DISABLED: { borderColor: 'var(--color-danger)', color: 'var(--color-danger)' },
};

function InviteMemberForm() {
  const invite = useInviteMember();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roleName, setRoleName] = useState<SystemRoleName>('SALES');
  const [title, setTitle] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  function clearFieldError(key: string) {
    setFieldErrors((f) => {
      if (!f[key]) return f;
      const { [key]: _removed, ...rest } = f;
      return rest;
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const errors: Record<string, string> = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      errors.email = 'Enter a valid email address.';
    if (displayName.trim().length < 1) errors.displayName = 'Enter their name.';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      await invite.mutateAsync({
        email: email.trim(),
        displayName: displayName.trim(),
        roleName,
        title: title.trim() || undefined,
      });
      setEmail('');
      setDisplayName('');
      setTitle('');
      toast.success('Invite sent.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'CONFLICT') {
        setFieldErrors({ email: 'This person is already a member of this workspace.' });
        setError('Please fix the highlighted fields.');
      } else if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
        } else {
          setError(err.message);
        }
      } else {
        setError(
          err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not send the invite. Please try again.',
        );
      }
    }
  }

  return (
    <Card title="Invite a teammate">
      <form
        onSubmit={onSubmit}
        style={{ display: 'flex', alignItems: 'end', gap: 'var(--space-3)', flexWrap: 'wrap' }}
        noValidate
      >
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            clearFieldError('email');
          }}
          error={fieldErrors.email}
        />
        <TextField
          label="Name"
          value={displayName}
          onChange={(e) => {
            setDisplayName(e.target.value);
            clearFieldError('displayName');
          }}
          error={fieldErrors.displayName}
        />
        <Select
          label="Role"
          options={ROLE_OPTIONS}
          value={roleName}
          onChange={(e) => setRoleName(e.target.value as SystemRoleName)}
        />
        <TextField
          label="Title (optional)"
          placeholder="e.g. Sales Rep"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Button type="submit" disabled={invite.isPending}>
          {invite.isPending ? 'Sending…' : 'Send invite'}
        </Button>
      </form>
      {error ? (
        <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-danger)' }}>{error}</p>
      ) : null}
    </Card>
  );
}

function MemberRow({
  member,
  canManage,
  isSelf,
}: {
  member: TeamMember;
  canManage: boolean;
  isSelf: boolean;
}) {
  const updateMember = useUpdateMember();
  const removeMember = useRemoveMember();
  const resendInvite = useResendInvite();
  const toast = useToast();

  async function onRoleChange(roleName: SystemRoleName) {
    try {
      await updateMember.mutateAsync({ tenantUserId: member.tenantUserId, patch: { roleName } });
      toast.success('Role updated.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the role.');
    }
  }

  async function onToggleStatus() {
    const next = member.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE';
    try {
      await updateMember.mutateAsync({
        tenantUserId: member.tenantUserId,
        patch: { status: next },
      });
      toast.success(next === 'ACTIVE' ? 'Member re-enabled.' : 'Member disabled.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the status.');
    }
  }

  async function onResend() {
    try {
      await resendInvite.mutateAsync(member.tenantUserId);
      toast.success('Invite resent.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not resend the invite.');
    }
  }

  async function onRemove() {
    if (!window.confirm(`Remove ${member.displayName} from this workspace?`)) return;
    try {
      await removeMember.mutateAsync(member.tenantUserId);
      toast.success('Member removed.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not remove the member.');
    }
  }

  const busy = updateMember.isPending || removeMember.isPending || resendInvite.isPending;

  return (
    <tr>
      <td>
        {member.displayName}
        {isSelf ? <span style={{ color: 'var(--color-text-muted)' }}> (you)</span> : null}
      </td>
      <td>{member.email}</td>
      <td>
        {canManage && !isSelf ? (
          <select
            className="ne-input"
            style={{ width: 'auto' }}
            aria-label={`Role for ${member.displayName}`}
            value={member.roles[0] ?? ''}
            disabled={busy}
            onChange={(e) => void onRoleChange(e.target.value as SystemRoleName)}
          >
            {ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ) : (
          member.roles.join(', ') || '—'
        )}
      </td>
      <td>{member.title ?? '—'}</td>
      <td>
        <span className="ne-badge" style={STATUS_BADGE_STYLE[member.status]}>
          {member.status}
        </span>
      </td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {!canManage || isSelf ? null : (
          <>
            {member.status === 'INVITED' ? (
              <Button variant="secondary" disabled={busy} onClick={() => void onResend()}>
                Resend invite
              </Button>
            ) : (
              <Button variant="secondary" disabled={busy} onClick={() => void onToggleStatus()}>
                {member.status === 'ACTIVE' ? 'Disable' : 'Enable'}
              </Button>
            )}{' '}
            <Button variant="secondary" disabled={busy} onClick={() => void onRemove()}>
              Remove
            </Button>
          </>
        )}
      </td>
    </tr>
  );
}

export function TeamPage() {
  const { hasPermission, tenantUserId } = useAuth();
  const canManage = hasPermission('users.manage');
  const members = useMembers();

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 900 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Team</h1>

      {canManage ? <InviteMemberForm /> : null}
      {canManage ? <DefaultAssigneesCard /> : null}

      <Card>
        {members.isPending ? (
          <PageState kind="loading" message="Loading team…" />
        ) : members.isError ? (
          <PageState
            kind="error"
            message={members.error.message}
            onRetry={() => void members.refetch()}
          />
        ) : members.data.items.length === 0 ? (
          <PageState kind="empty" title="No members" />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="ne-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {members.data.items.map((m) => (
                  <MemberRow
                    key={m.tenantUserId}
                    member={m}
                    canManage={canManage}
                    isSelf={m.tenantUserId === tenantUserId}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
